import React, { useState, useRef } from "react";
import theme, { colors, RightContainerStyled } from "../../assets/styles/theme";
import Typography from "@mui/material/Typography";
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
} from "@mui/material";
import DeleteIconSvg from "../../assets/images/icon-delete.svg";
import AttachmentIcon from "@mui/icons-material/Attachment";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FileLink } from "./modals/JsonFileViewer";

interface ConnectionFormProps {
  connection: any;
  dataSourceName:
    | "Google Ads"
    | "Google Analytics"
    | "Facebook Ads"
    | "LinkedIn Ads"
    | "Simpli.fi";
  index: number;
  isEditing: boolean;
  onDelete: () => void;
  onSave: (values: any) => void;
  onCancel?: () => void;
  onChange: (values: any, connectionId: string) => void;
  formRef?: React.Ref<ConnectionFormRef>;
  handleErrorChange: (errors: any) => void;
  existingConnections: any;
  modifiedConnections: any;
  deletedConnections: any;
}

interface ConnectionFormRef {
  submitForm: () => void;
  isValid: boolean;
  dirty: boolean;
  values: any;
}

export const ConnectionForm: React.FC<ConnectionFormProps> = ({
  connection,
  dataSourceName,
  index,
  isEditing,
  onDelete,
  onSave,
  onCancel,
  onChange,
  formRef,
  handleErrorChange,
  existingConnections,
  modifiedConnections,
  deletedConnections,
}) => {
  const createValidationSchema = (dataSourceName: string) => {
    const baseValidation = {
      connectionName: Yup.string().required("Connection name is required"),
    };

    switch (dataSourceName) {
      case "Google Ads":
        return Yup.object({
          ...baseValidation,
          developerToken: Yup.string().required("Developer token is required"),
          customerLoginID: Yup.string().required(
            "Customer Login ID is required"
          ),
          jsonFile: Yup.mixed().required("JSON file is required"),
        });

      case "Google Analytics":
        return Yup.object({
          ...baseValidation,
          jsonFile: Yup.mixed().required("JSON file is required"),
        });

      case "Facebook Ads":
      case "LinkedIn Ads":
        return Yup.object({
          ...baseValidation,
          accessToken: Yup.string().required("Access token is required"),
        });

      case "Simpli.fi":
        return Yup.object({
          ...baseValidation,
          appKey: Yup.string().required("App key is required"),
          userKey: Yup.string().required("User key is required"),
        });

      default:
        return Yup.object(baseValidation);
    }
  };

  const parseConnectionJson = (jsonString: any) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("Error parsing connection JSON:", error);
      return {};
    }
  };

  const connectionData = parseConnectionJson(connection.connectionJson);

  const initialValues = {
    connectionName: connection.connectionName || "",
    ...(dataSourceName === "Google Ads" && {
      developerToken: connectionData.DeveloperToken || "",
      customerLoginID: connectionData.CustomerID || "",
      jsonFile: connectionData.jsonFile || connectionData.JSON || "",
    }),
    ...(dataSourceName === "Google Analytics" && {
      jsonFile: connectionData.jsonFile || connectionData.JSON || "",
    }),
    ...(dataSourceName === "Facebook Ads" && {
      accessToken: connectionData.FacebookOAuthAccessToken || "",
    }),
    ...(dataSourceName === "LinkedIn Ads" && {
      accessToken: connectionData.LinkedInOAuthAccessToken || "",
    }),
    ...(dataSourceName === "Simpli.fi" && {
      appKey: connectionData.SimplifiAppKey || "",
      userKey: connectionData.SimplifiUserKey || "",
    }),
  };

  const [isModified, setIsModified] = useState(false);
  const [originalValues, setOriginalValues] = useState<any>(initialValues);

  const filterOutDeletedConnections = (
    modifiedConnections: any,
    deletedConnections: any
  ) =>
    Object.keys(modifiedConnections)
      .filter((key) => !deletedConnections.includes(key))
      .reduce((obj: any, key: any) => {
        obj[key] = modifiedConnections[key];
        return obj;
      }, {});

  function mergeConnectionNames(dataList: any, connectionNameMap: any) {
    return dataList.map((item: any) => {
      const connectionId = item.dataSourceConnectionId;

      // Check if the connection ID exists in the connectionNameMap
      if (connectionNameMap.hasOwnProperty(connectionId)) {
        // Create a new object with the additional connection name
        return {
          ...item,
          connectionName: connectionNameMap[connectionId].connectionName,
        };
      }

      // If no matching connection name is found, return the original item
      return item;
    });
  }

  const formik: any = useFormik({
    initialValues,
    validationSchema: createValidationSchema(dataSourceName),
    validate: (values) => {
      console.log("deletedConnections", deletedConnections);

      const errors: { connectionName?: string } = {};
      const updatedList = mergeConnectionNames(
        existingConnections,
        filterOutDeletedConnections(modifiedConnections, deletedConnections)
      );
      const isDuplicateName = updatedList.some(
        (conn: any) =>
          conn.dataSourceConnectionId !== connection.dataSourceConnectionId &&
          conn.connectionName.toLowerCase() ===
            values.connectionName.toLowerCase()
      );

      console.log("isDuplicateName", isDuplicateName);

      if (isDuplicateName) {
        errors.connectionName =
          "Connection name must be unique within this Data Source";
      }

      return errors;
    },
    onSubmit: (values) => {
      console.log("onSubmit values", values);
    },
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
  });

  React.useEffect(() => {
    if (formik.values !== originalValues) {
      setIsModified(true);
      onChange(formik.values, connection.dataSourceConnectionId);
    }
  }, [formik.values]);

  React.useEffect(() => {
    if (formik.errors) {
      handleErrorChange(formik.errors);
    }
  }, [formik.errors]);

  React.useEffect(() => {
    if (onCancel) {
      formik.resetForm();
      setIsModified(false);
    }
  }, [onCancel]);

  const hasError = (fieldName: any) =>
    formik.touched[fieldName] && Boolean(formik.errors[fieldName]);

  const getErrorMessage = (fieldName: any) => {
    if (hasError(fieldName)) {
      return formik.errors[fieldName];
    }
    return (
      <span>
        Required<span className="text-red">*</span>
      </span>
    );
  };

  const handleConnectionNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.handleChange(e);
    formik.setFieldTouched("connectionName", true, false);
  };

  const renderFields = () => {
    switch (dataSourceName) {
      case "Google Ads":
        return (
          <Grid container spacing={3}>
            <Grid sm={6} md={6} item sx={{ mb: -1 }}>
              <Box className="d-flex">
                <Box className="count-wrapper justify-content-start">
                  <Typography
                    variant="body2"
                    className="fs-16"
                    sx={{ color: colors.secondaryText }}
                  >
                    {index}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  id={`connectionName-${connection.dataSourceConnectionId}`}
                  name="connectionName"
                  label="Connection"
                  value={formik.values.connectionName}
                  onChange={handleConnectionNameChange}
                  onBlur={formik.handleBlur}
                  error={hasError("connectionName")}
                  helperText={getErrorMessage("connectionName")}
                  disabled={!isEditing}
                />
              </Box>
            </Grid>
            <Grid sm={6} md={6} item sx={{ mb: -1 }}>
              <Box className="d-flex">
                <TextField
                  fullWidth
                  id={`developerToken-${connection.dataSourceConnectionId}`}
                  name="developerToken"
                  label="Developer Token"
                  type={isEditing ? "text" : "password"}
                  value={formik.values.developerToken}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={hasError("developerToken")}
                  helperText={getErrorMessage("developerToken")}
                  disabled={!isEditing}
                />
                <Box
                  className="count-wrapper justify-content-end"
                  sx={{ ml: 0.6 }}
                ></Box>
              </Box>
            </Grid>

            <Grid sm={6} md={6} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <Box className="count-wrapper justify-content-start"></Box>

                <TextField
                  fullWidth
                  id={`customerLoginID-${connection.dataSourceConnectionId}`}
                  name="customerLoginID"
                  label="Customer Login ID"
                  type={isEditing ? "text" : "password"}
                  value={formik.values.customerLoginID}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={hasError("customerLoginID")}
                  helperText={getErrorMessage("customerLoginID")}
                  disabled={!isEditing}
                />
              </Box>
            </Grid>
            <Grid sm={6} md={6} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <Box className="adsFileUploadWrapper">
                  <TextField
                    fullWidth
                    id={`jsonFile-${connection.dataSourceConnectionId}`}
                    name="jsonFile"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const file = event.currentTarget.files?.[0];
                      if (file) {
                        if (
                          file &&
                          file.type !== "application/json" &&
                          !file.name.endsWith(".json")
                        ) {
                          formik.setFieldError(
                            "jsonFile",
                            "Only JSON files are allowed"
                          );
                          event.currentTarget.value = ""; // Clear the invalid file
                          return;
                        }

                        formik.setFieldError("jsonFile", "");
                        formik.setFieldValue("jsonFile", file, true);
                        formik.setFieldTouched("jsonFile", true, false);
                      }
                    }}
                    onBlur={() => {
                      formik.validateField("jsonFile");
                      formik.setFieldTouched("jsonFile", true, true);
                    }}
                    error={hasError("jsonFile")}
                    helperText={getErrorMessage("jsonFile")}
                    InputProps={{
                      readOnly: !isEditing,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachmentIcon
                            sx={{
                              color: colors.primaryText,
                              fontSize: "1rem",
                              rotate: "270deg",
                            }}
                          />
                          <label
                            className="labelJsonFileName"
                            htmlFor={`jsonFile-${connection.dataSourceConnectionId}`}
                          >
                            {formik.values.jsonFile &&
                            formik.values.jsonFile.name
                              ? formik.values.jsonFile.name
                              : formik.values.jsonFile
                              ? "Service Account Json File"
                              : "Service Account Json File"}
                          </label>
                        </InputAdornment>
                      ),

                      inputProps: {
                        accept: ".json,application/json",
                      },
                    }}
                    title=" "
                    type="file"
                    disabled={!isEditing}
                  />

                  <Box className="linkWrapper">
                    {isEditing &&
                      (formik.values.jsonFile &&
                      formik.values.jsonFile != "" ? (
                        // For newly uploaded files
                        <FileLink
                          fileOrData={formik.values.jsonFile}
                          linkText="View"
                        />
                      ) : connectionData.jsonFile ? (
                        // For existing JSON data stored in connectionData
                        <FileLink
                          fileOrData={connectionData.jsonFile}
                          linkText="View"
                        />
                      ) : null)}
                    {isEditing && formik.values.jsonFile != "" && (
                      <label
                        htmlFor={`jsonFile-${connection.dataSourceConnectionId}`}
                      >
                        <Link
                          className="fs-13 underLine"
                          sx={{ ml: 1.2, cursor: "pointer" }}
                          component="span"
                        >
                          Replace
                        </Link>
                      </label>
                    )}
                  </Box>
                </Box>

                <Box
                  className="count-wrapper justify-content-end"
                  sx={{ ml: 0.6 }}
                >
                  {isEditing && (
                    <IconButton
                      aria-label="delete"
                      size="medium"
                      className="iconBtn"
                      onClick={onDelete}
                    >
                      <img src={DeleteIconSvg} alt="Delete Icon" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid sm={12} className="borderSeprator">
              <Divider sx={{ mt: -0.5, mb: 3.5 }} />
            </Grid>
          </Grid>
        );

      case "Google Analytics":
        return (
          <Grid container spacing={3}>
            <Grid sm={6} md={6} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <Box className="count-wrapper justify-content-start">
                  <Typography
                    variant="body2"
                    className="fs-16"
                    sx={{ color: colors.secondaryText }}
                  >
                    {index}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  id={`connectionName-${connection.dataSourceConnectionId}`}
                  name="connectionName"
                  label="Connection"
                  value={formik.values.connectionName}
                  onChange={handleConnectionNameChange}
                  onBlur={formik.handleBlur}
                  error={hasError("connectionName")}
                  helperText={getErrorMessage("connectionName")}
                  disabled={!isEditing}
                />
              </Box>
            </Grid>
            <Grid sm={6} md={6} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <Box className="adsFileUploadWrapper">
                  <TextField
                    fullWidth
                    id={`jsonFile-${connection.dataSourceConnectionId}`}
                    name="jsonFile"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const file = event.currentTarget.files?.[0];
                      if (file) {
                        if (
                          file &&
                          file.type !== "application/json" &&
                          !file.name.endsWith(".json")
                        ) {
                          formik.setFieldError(
                            "jsonFile",
                            "Only JSON files are allowed"
                          );
                          event.currentTarget.value = ""; // Clear the invalid file
                          return;
                        }

                        formik.setFieldError("jsonFile", "");
                        formik.setFieldValue("jsonFile", file, true);
                        formik.setFieldTouched("jsonFile", true, false);
                      }
                    }}
                    onBlur={() => {
                      formik.validateField("jsonFile");
                      formik.setFieldTouched("jsonFile", true, true);
                    }}
                    error={hasError("jsonFile")}
                    helperText={getErrorMessage("jsonFile")}
                    InputProps={{
                      readOnly: !isEditing,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachmentIcon
                            sx={{
                              color: colors.primaryText,
                              fontSize: "1rem",
                              rotate: "270deg",
                            }}
                          />
                          <label
                            className="labelJsonFileName"
                            htmlFor={`jsonFile-${connection.dataSourceConnectionId}`}
                          >
                            {formik.values.jsonFile &&
                            formik.values.jsonFile.name
                              ? formik.values.jsonFile.name
                              : formik.values.jsonFile
                              ? "Service Account Json File"
                              : "Service Account Json File"}
                          </label>
                        </InputAdornment>
                      ),
                      inputProps: {
                        accept: ".json,application/json",
                      },
                    }}
                    title=" "
                    type="file"
                    disabled={!isEditing}
                  />

                  <Box className="linkWrapper">
                    {isEditing &&
                      (formik.values.jsonFile &&
                      formik.values.jsonFile != "" ? (
                        // For newly uploaded files
                        <FileLink
                          fileOrData={formik.values.jsonFile}
                          linkText="View"
                        />
                      ) : connectionData.jsonFile ? (
                        // For existing JSON data stored in connectionData
                        <FileLink
                          fileOrData={connectionData.jsonFile}
                          linkText="View"
                        />
                      ) : null)}

                    {isEditing && formik.values.jsonFile != "" && (
                      <label
                        htmlFor={`jsonFile-${connection.dataSourceConnectionId}`}
                      >
                        <Link
                          className="fs-13 underLine"
                          sx={{ ml: 1.2, cursor: "pointer" }}
                          component="span"
                        >
                          Replace
                        </Link>
                      </label>
                    )}
                  </Box>
                </Box>

                <Box
                  className="count-wrapper justify-content-end"
                  sx={{ ml: 0.6 }}
                >
                  {isEditing && (
                    <IconButton
                      aria-label="delete"
                      size="medium"
                      className="iconBtn"
                      onClick={onDelete}
                    >
                      <img src={DeleteIconSvg} alt="Delete Icon" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid sm={12} className="borderSeprator">
              <Divider sx={{ mt: -0.5, mb: 3.5 }} />
            </Grid>
          </Grid>
        );

      case "Facebook Ads":
      case "LinkedIn Ads":
        return (
          <Grid container spacing={3}>
            <Grid sm={6} md={6} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <Box className="count-wrapper justify-content-start">
                  <Typography
                    variant="body2"
                    className="fs-16"
                    sx={{ color: colors.secondaryText }}
                  >
                    {index}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  id={`connectionName-${connection.dataSourceConnectionId}`}
                  name="connectionName"
                  label="Connection"
                  value={formik.values.connectionName}
                  onChange={handleConnectionNameChange}
                  onBlur={formik.handleBlur}
                  error={hasError("connectionName")}
                  helperText={getErrorMessage("connectionName")}
                  disabled={!isEditing}
                />
              </Box>
            </Grid>
            <Grid sm={6} md={6} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <TextField
                  fullWidth
                  id={`accessToken-${connection.dataSourceConnectionId}`}
                  name="accessToken"
                  label="Access Token"
                  type={isEditing ? "text" : "password"}
                  value={formik.values.accessToken}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={hasError("accessToken")}
                  helperText={getErrorMessage("accessToken")}
                  disabled={!isEditing}
                />

                <Box
                  className="count-wrapper justify-content-end"
                  sx={{ ml: 0.6 }}
                >
                  {isEditing && (
                    <IconButton
                      aria-label="delete"
                      size="medium"
                      className="iconBtn"
                      onClick={onDelete}
                    >
                      <img src={DeleteIconSvg} alt="Delete Icon" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid sm={12} className="borderSeprator">
              <Divider sx={{ mt: -0.5, mb: 3.5 }} />
            </Grid>
          </Grid>
        );

      case "Simpli.fi":
        return (
          <Grid container spacing={3}>
            <Grid sm={4} md={4} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <Box className="count-wrapper justify-content-start">
                  <Typography
                    variant="body2"
                    className="fs-16"
                    sx={{ color: colors.secondaryText }}
                  >
                    {index}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  id={`connectionName-${connection.dataSourceConnectionId}`}
                  name="connectionName"
                  label="Connection"
                  value={formik.values.connectionName}
                  onChange={handleConnectionNameChange}
                  onBlur={formik.handleBlur}
                  error={hasError("connectionName")}
                  helperText={getErrorMessage("connectionName")}
                  disabled={!isEditing}
                />
              </Box>
            </Grid>
            <Grid sm={4} md={4} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <TextField
                  fullWidth
                  id={`appKey-${connection.dataSourceConnectionId}`}
                  name="appKey"
                  label="App Key"
                  type={isEditing ? "text" : "password"}
                  value={formik.values.appKey}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={hasError("appKey")}
                  helperText={getErrorMessage("appKey")}
                  disabled={!isEditing}
                />
              </Box>
            </Grid>
            <Grid sm={4} md={4} item sx={{ mb: 2 }}>
              <Box className="d-flex">
                <TextField
                  fullWidth
                  id={`userKey-${connection.dataSourceConnectionId}`}
                  name="userKey"
                  label="User Key"
                  type={isEditing ? "text" : "password"}
                  value={formik.values.userKey}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={hasError("userKey")}
                  helperText={getErrorMessage("userKey")}
                  disabled={!isEditing}
                />

                <Box
                  className="count-wrapper justify-content-end"
                  sx={{ ml: 0.6 }}
                >
                  {isEditing && (
                    <IconButton
                      aria-label="delete"
                      size="medium"
                      className="iconBtn"
                      onClick={onDelete}
                    >
                      <img src={DeleteIconSvg} alt="Delete Icon" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid sm={12} className="borderSeprator">
              <Divider sx={{ mt: -0.5, mb: 3.5 }} />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  React.useImperativeHandle(formRef, () => ({
    submitForm: formik.handleSubmit,
    isValid: formik.isValid,
    dirty: formik.dirty,
    values: formik.values,
    errors: formik.errors,
    validateForm: formik.validateForm,
  }));

  return <form onSubmit={formik.handleSubmit}>{renderFields()}</form>;
};
